import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Timestamp, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { BsCheck2Circle } from 'react-icons/bs';
import { MdOutlineSmsFailed } from 'react-icons/md';
import Modal from '../../../components/Modal/Modal';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa6';

const ProjectView = () => {
    const { id } = useParams();
    const [project, setProject] = useState({});
    const [images, setImages] = useState([]);
    const [isDone, setIsDone] = useState(false);
    const [isVisible, setIsVisible] = useState([]);

    useEffect(() => {
        const fetchProjectData = async () => {
            const docRef = doc(db, 'projects', id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const projectData = docSnap.data();
                setProject(projectData);
                setImages(projectData.images || []);
                setIsVisible(projectData.isVisible);

            } else {
                console.log("Aucun document trouvé !");
            }
        };
        fetchProjectData();
    }, [id]);

    //Archiver un article
    const [isArchiving, setIsArchiving] = useState(false);

    const archivateProject = async (event) => {
        event.preventDefault();
        setIsArchiving(true); // Afficher le loader

        const articleDocRef = doc(db, 'projects', id);

        try {
            // Attendre la mise à jour de l'article
            await updateDoc(articleDocRef, {
                isVisible: false,
                lastModif: Timestamp.now(),
            });

            // La mise à jour de l'article est réussie, nous pouvons réinitialiser setIsArchiving
            setIsArchiving(false);
            navigate(-1);
        } catch (error) {
            setIsArchiving(false);
            console.error("Error updating article: ", error);
            // Vous pouvez ajouter une logique de gestion d'erreur ici, comme afficher un message d'erreur à l'utilisateur.
        }
    };

    //Désarchiver un article
    const [isDisarchiving, setIsDisarchiving] = useState(false);

    const desarchivateProject = async (event) => {
        event.preventDefault();
        setIsDisarchiving(true); // Afficher le loader

        const articleDocRef = doc(db, 'projects', id);

        try {
            // Attendre la mise à jour de l'article
            await updateDoc(articleDocRef, {
                isVisible: true,
                lastModif: Timestamp.now(),
            });

            // La mise à jour de l'article est réussie, nous pouvons réinitialiser setIsArchiving
            setIsDisarchiving(false);
            navigate(-1);
        } catch (error) {
            setIsDisarchiving(false);
            console.error("Error updating article: ", error);
            // Vous pouvez ajouter une logique de gestion d'erreur ici, comme afficher un message d'erreur à l'utilisateur.
        }
    };

    const deleteEvent = async () => {
        try {

            // Accédez au document dans Firestore
            const docRef = doc(db, 'events', id);

            // Supprimez le document
            await deleteDoc(docRef);
            setIsDone(true);
            setOpen(true);

            // Vous pouvez également effectuer d'autres actions après la suppression, comme rediriger l'utilisateur ou afficher un message de confirmation.
        } catch (error) {
            setIsDone(false);
            setOpen(true);
            // Gérez l'erreur, par exemple en affichant un message d'erreur à l'utilisateur.
        }
    };

    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const handleClose = () => {
        setOpen(false);
        navigate(-1);
    }

    return (
        <motion.div
            className='flex flex-col gap-10 w-full min-h-screen bg-white lg:p-[5rem] p-[1.25rem]'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >

            <div className="flex lg:flex-row flex-col w-full lg:gap-[50px] gap-10 lg:mt-[100px] mt-[100px]">

                {/* Gauche */}
                <motion.section
                    className="lg:w-2/5 w-full"
                    initial={{ x: -40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    <button
                        onClick={() => navigate(-1)}
                        className="text-black text-[20px] hover:opacity-70 transition"
                        title="Retour"
                    >
                        <FaArrowLeft />
                    </button>
                    <div className="flex flex-col">
                        <p className="text-lightgrey2 uppercase">{project.projectType}</p>
                        <p className="text-black uppercase lg:text-[42px] text-[32px] font-bold ppmori">
                            {project.projectName}
                        </p>
                        <p className="flex gap-5 text-lightgrey2">Year <span className='text-black'>{project.year}</span></p>

                        <div
                            className="lg:hidden block w-full mb-5 aspect-[5/4] bg-cover bg-center rounded-lg"
                            style={{ backgroundImage: `url(${project.coverUrl})` }}
                        />
                    </div>

                    <motion.p
                        className="text-black ppmori lg:mt-[30px] lg:mb-[50px] mb-5"
                        dangerouslySetInnerHTML={{ __html: project?.content }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                    />

                    <div className="flex flex-col lg:gap-[50px] gap-5">
                        <div className="grid justify-between grid-cols-2 w-full">
                            <p className='text-lightgrey2'>
                                Client <br />
                                <span className='text-black'>{project.client}</span>
                            </p>

                            <p className='text-lightgrey2'>
                                Mission <br />
                                <span className='text-black'>
                                    {Array.isArray(project.missions) && project.missions.length > 0 ? (
                                        project.missions.map((mission, index) => (
                                            <div key={index}>{mission}</div>
                                        ))
                                    ) : (
                                        <span className="text-gray-500">---</span>
                                    )}
                                </span>
                            </p>
                        </div>

                        {/* Liens */}
                        <div className="grid justify-between grid-cols-2 w-full">
                            {project.projectWebsite && (
                                <a className="text-lightgrey2 hover:text-black w-fit font-light lg:text-[20px] text-[24px]"
                                    href={project.projectWebsite} target="_blank" rel="noopener noreferrer">
                                    Website
                                </a>
                            )}
                            {project.projectInstagram && (
                                <a className="text-lightgrey2 hover:text-black w-fit font-light lg:text-[20px] text-[24px]"
                                    href={project.projectInstagram} target="_blank" rel="noopener noreferrer">
                                    Instagram
                                </a>
                            )}
                        </div>
                    </div>
                </motion.section>

                {/* Droite */}
                <motion.section
                    className="flex flex-col gap-10 lg:w-3/5 w-full"
                    initial={{ x: 40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="flex flex-col gap-5">
                        {/* Cover principale */}
                        {project.coverUrl && (
                            <motion.div
                                className="lg:block hidden w-full aspect-[5/4] bg-cover bg-center rounded-lg"
                                style={{ backgroundImage: `url(${project.coverUrl})` }}
                                initial={{ scale: 0.98, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            />
                        )}

                        {/* Images en pattern */}
                        {Array.isArray(project.images) &&
                            project.images.reduce((acc, url, index) => {
                                if (index % 4 === 0) acc.push([]);
                                acc[acc.length - 1].push(url);
                                return acc;
                            }, []).map((group, groupIndex) => (
                                <motion.div
                                    key={groupIndex}
                                    className="flex flex-col gap-5"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2 + groupIndex * 0.2 }}
                                >
                                    <div className="grid grid-cols-3 gap-2.5">
                                        {group.slice(0, 3).map((url, index) => (
                                            <div
                                                key={index}
                                                className="w-full h-[40dvh] bg-cover bg-center rounded-lg"
                                                style={{ backgroundImage: `url(${url})` }}
                                            />
                                        ))}
                                    </div>

                                    {group[3] && (
                                        <div
                                            className="w-full h-[60dvh] bg-cover bg-center rounded-lg"
                                            style={{ backgroundImage: `url(${group[3]})` }}
                                        />
                                    )}
                                </motion.div>
                            ))}
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-between font-bold">
                        <Link className='lg:text-[38px] ppmori uppercase text-lightgrey2 leading-none'>Previous <br /> Project</Link>
                        <Link className='lg:text-[38px] ppmori uppercase text-right leading-none'>Next <br /> Project</Link>
                    </div>
                </motion.section>

            </div>

            <div className="flex gap-5 fixed bottom-10 right-[30px] ">
                <Link to={"/dashboard/projectapp/edit/" + id} className='buttonblack'>Modifier</Link>

                {isVisible ? (
                    <button
                        onClick={archivateProject}
                        className='buttonblack'
                        type='button'
                    >
                        {isArchiving ? (
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <p>Archiver</p>
                        )}
                    </button>

                ) : (

                    <button
                        onClick={desarchivateProject}
                        className='buttonblack'
                        type='button'
                    >
                        {isDisarchiving ? (
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <p>Désarchiver</p>
                        )}
                    </button>
                )}

                {isVisible === false && (
                    <button
                        className='buttonblack'
                        type='button'
                        onClick={deleteEvent}
                    >
                        Supprimer
                    </button>
                )}
            </div>


            {open &&
                <Modal isOpen={open} onClose={handleClose}>
                    <center>
                        <div className='flex flex-col gap-4 items-center justify-center'>
                            {isDone ? (
                                <>
                                    <BsCheck2Circle className='text-green-700 text-[50px]' />

                                    <p>L'évènement a été supprimé !</p>
                                </>
                            ) : (
                                <>
                                    <MdOutlineSmsFailed className='text-red-700 text-[50px]' />

                                    <p>Une erreur s'est produite, veuillez réessayer !</p>
                                </>
                            )}
                            <button
                                className='buttonviolet w-[50px!important]'
                                type='submit'
                                onClick={handleClose}
                            >OK</button>
                        </div>
                    </center>
                </Modal>
            }

        </motion.div>
    );
};

export default ProjectView;